import { createContext, useContext, useMemo, useSyncExternalStore } from 'react';
import { creerClient, type Client } from '../../api/client.js';
import type { Ecran } from '../../api/contrat.js';
import type { ComposantEcran } from '../../app/ecrans.js';
import { COULEURS } from '../../app/styles.js';
import { peutValiderArbre } from './arbre-provisoire.js';
import type { Extractor, Transcriber } from './contrat.js';
import { creerControleurAccueil } from './controleur.js';
import { VueAccueil } from './vues.js';

export interface ContexteAccueil {
  client?: Client;
  /** `fetch` de la coquille : sert aussi aux écritures à clé d'idempotence stable (une par personne et par lien). */
  fetch?: typeof fetch;
  baseUrl?: string;
  transcripteur?: Transcriber;
  extracteur?: Extractor;
  dossierId?: string | null;
}

/** La coquille fournit ici le client, les ports IA réels et le dossier courant ; à défaut, doublures du contrat. */
export const ContexteAccueilEcran = createContext<ContexteAccueil>({});

const refus = async (): Promise<never> => {
  throw new Error('Transcription des vocaux indisponible (RFC-L63-001, point 2).');
};
const INDISPONIBLE: Transcriber = { transcribe: refus };
const EXTRACTION_INDISPONIBLE: Extractor = { extractFamily: refus, draftMessage: refus };

/** Écran d'accueil par vocal, monté dans la coquille dès que le contrat lui attribue un identifiant (RFC-L63-001). */
export function Accueil({ ecran }: { ecran: Ecran }) {
  const ctx = useContext(ContexteAccueilEcran);
  const controleur = useMemo(() => {
    const f: typeof fetch = ctx.fetch ?? ((...args) => globalThis.fetch(...args));
    const base = ctx.baseUrl !== undefined ? { baseUrl: ctx.baseUrl } : {};
    return creerControleurAccueil({
      client: ctx.client ?? creerClient({ fetch: f, ...base }),
      clientAvecCle: (cle) => creerClient({ fetch: f, ...base, genererCle: () => cle }),
      // C-21 : jamais de doublure en production ; sans port fourni par la coquille, le vocal n'est pas offert (et refusé s'il arrivait).
      transcripteur: ctx.transcripteur ?? INDISPONIBLE,
      extracteur: ctx.extracteur ?? EXTRACTION_INDISPONIBLE,
      role: ecran.role,
      aujourdhui: () => new Date().toISOString().slice(0, 10),
    });
  }, [ctx.client, ctx.fetch, ctx.baseUrl, ctx.transcripteur, ctx.extracteur, ecran.role]);
  const etat = useSyncExternalStore(controleur.subscribe, controleur.getEtat, controleur.getEtat);
  // Couloir G, point 6 : le vocal va au dossier ouvert (`?dossier=`, depuis le guide), ou à celui que l'entretien vient de créer.
  const dossierDeLAdresse = new URLSearchParams(globalThis.location?.search ?? '').get('dossier')?.trim() || null;
  const dossierVocal = ctx.dossierId ?? dossierDeLAdresse ?? etat.enregistrement?.dossierId ?? null;
  return (
    <section data-ecran={ecran.id} aria-label={ecran.titre}>
      <h1 style={{ color: COULEURS.marine, fontSize: 28 }}>{ecran.titre}</h1>
      <VueAccueil
        etat={etat}
        controleur={controleur}
        peutValider={peutValiderArbre(ecran.role)}
        dossierId={ctx.dossierId ?? null}
        onReessayer={controleur.reessayer}
        vocalDisponible={ctx.transcripteur !== undefined}
        dossierVocal={dossierVocal}
      />
    </section>
  );
}

/** D16 « Entretien d’accueil » (constat C-21) : l'accueil par vocal ou formulaire, joignable à /accueil. */
export const ecrans: Readonly<Record<string, ComposantEcran>> = { D16: Accueil };
